/**
 * CountersignSettingsDialog — ★ Fase 14: firma + timbro aziendali.
 *
 * L'amministrazione vede e aggiorna gli elementi usati per la controfirma
 * 1-click dei contratti: firma corsiva blu (default «Richard Demichele») e
 * timbro HCF blu (logo). Entrambi sostituibili caricando un'immagine PNG.
 */
import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Skeleton } from '../ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Stamp, Loader2, Upload, Save } from 'lucide-react';

const MAX_IMG = 2 * 1024 * 1024; // 2 MB

function ImageSlot({ label, current, pendingB64, onPick, testId }) {
  const src = pendingB64
    ? `data:image/png;base64,${pendingB64}`
    : (current ? `data:image/png;base64,${current}` : null);
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-wide">{label}</Label>
      <div className="rounded-md border border-border bg-white p-3 min-h-[90px] grid place-items-center">
        {src ? (
          <img src={src} alt={label} className="max-h-20 object-contain" data-testid={`${testId}-preview`} />
        ) : (
          <Skeleton className="h-16 w-full" />
        )}
      </div>
      <label className="inline-flex items-center gap-1.5 text-xs text-[hsl(var(--primary))] cursor-pointer hover:underline">
        <Upload className="w-3.5 h-3.5" />
        Sostituisci (PNG con sfondo trasparente)
        <input
          type="file" className="hidden" accept="image/png,image/webp"
          onChange={onPick}
          data-testid={`${testId}-input`}
        />
      </label>
      {pendingB64 && <span className="text-[10px] text-amber-700 ml-2">nuova immagine — salva per confermare</span>}
    </div>
  );
}

export function CountersignSettingsDialog({ open, onClose }) {
  const [data, setData] = useState(null);
  const [signerName, setSignerName] = useState('');
  const [newSig, setNewSig] = useState(null);
  const [newStamp, setNewStamp] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setData(null); setNewSig(null); setNewStamp(null);
    api.get('/contracts/countersign-settings')
      .then((r) => { setData(r.data); setSignerName(r.data?.signer_name || ''); })
      .catch((e) => toast.error(e?.response?.data?.detail || 'Errore caricamento impostazioni'));
  }, [open]);

  const pick = (setter) => async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > MAX_IMG) { toast.error('Immagine troppo grande (max 2 MB).'); return; }
    const b64 = await new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(String(fr.result).split(',')[1]);
      fr.onerror = reject;
      fr.readAsDataURL(file);
    });
    setter(b64);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/contracts/countersign-settings', {
        signer_name: signerName.trim() || undefined,
        signature_b64: newSig || undefined,
        stamp_b64: newStamp || undefined,
      });
      toast.success('Firma aziendale aggiornata');
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="countersign-settings-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Stamp className="w-5 h-5 text-[hsl(var(--primary))]" /> Firma aziendale
          </DialogTitle>
          <DialogDescription>
            Timbro e firma usati per la controfirma 1-click dei contratti
            (apposti nel PDF a doppia firma inviato al cliente).
          </DialogDescription>
        </DialogHeader>
        {!data ? (
          <div className="space-y-3"><Skeleton className="h-24" /><Skeleton className="h-24" /></div>
        ) : (
          <div className="space-y-4">
            <ImageSlot
              label="Timbro aziendale (House Cash Flows)"
              current={data.stamp_b64}
              pendingB64={newStamp}
              onPick={pick(setNewStamp)}
              testId="countersign-stamp"
            />
            <ImageSlot
              label="Firma (blu, stile corsivo)"
              current={data.signature_b64}
              pendingB64={newSig}
              onPick={pick(setNewSig)}
              testId="countersign-signature"
            />
            <div>
              <Label className="text-xs uppercase tracking-wide">Nome firmatario</Label>
              <Input
                value={signerName}
                onChange={(e) => setSignerName(e.target.value)}
                className="mt-1"
                data-testid="countersign-signer-name"
              />
            </div>
            {data.updated_at && (
              <p className="text-[10px] text-muted-foreground">
                Ultimo aggiornamento: {new Date(data.updated_at).toLocaleString('it-IT')} · {data.updated_by}
              </p>
            )}
          </div>
        )}
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose} disabled={saving}>Chiudi</Button>
          <Button
            onClick={save}
            disabled={saving || !data}
            className="bg-[var(--gp-gold)] text-[var(--gp-forest)] hover:bg-[var(--gp-gold-soft)]"
            data-testid="countersign-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Salva
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
